import { useMemo } from 'react'
import { useCacheTable } from './hooks.js'
import { Vorschlagsfeld } from './Vorschlagsfeld.js'
import type { CacheRow } from '../net/localDb.js'
import {
  eigeneOrtschaften,
  eigeneStrassen,
  strassenVorschlaege,
  type Adresse,
  ortDarfFolgen,
  ortZuPlz,
  plzDarfFolgen,
  plzZuOrt,
  verzeichnisVorhanden,
  vorschlaege
} from '../ortschaften.js'

/**
 * Das Feldpaar «PLZ / Ort» – einmal gebaut, überall gleich.
 *
 * Vorher stand es viermal im Quelltext: im Rechnungsbildschirm mit Vorschlag,
 * bei den Kunden ohne, bei den Projekten ohne, in den Einstellungen ohne. Ein
 * Betrieb, der überall dasselbe eintippt, erlebt das als Willkür – und hat
 * recht damit.
 *
 * Was das Paar tut, ist eine einzige Zusage: vier Stellen getippt, Ort steht
 * da. Nicht mehr. Es korrigiert nichts, was jemand selbst geschrieben hat,
 * und es füllt nichts nach einer halben Postleitzahl.
 */

export function useOrtschaften(): Array<[string, string]> {
  const { rows: kunden } = useCacheTable<CacheRow>('customers')
  const { rows: projekte } = useCacheTable<CacheRow>('projects')
  return useMemo(
    () =>
      eigeneOrtschaften(
        kunden as unknown as Array<Record<string, unknown>>,
        projekte as unknown as Array<Record<string, unknown>>
      ).sort((a, b) => a[0].localeCompare(b[0])),
    [kunden, projekte]
  )
}

/**
 * Die Strassen, die dieser Betrieb schon einmal getippt hat.
 *
 * Dieselbe Quelle wie bei den Ortschaften: Kunden und Baustellen aus dem
 * lokalen Zwischenspeicher.
 */
export function useStrassen(): Adresse[] {
  const { rows: kunden } = useCacheTable<CacheRow>('customers')
  const { rows: projekte } = useCacheTable<CacheRow>('projects')
  return useMemo(
    () =>
      eigeneStrassen(
        kunden as unknown as Array<Record<string, unknown>>,
        projekte as unknown as Array<Record<string, unknown>>
      ),
    [kunden, projekte]
  )
}

/**
 * Das Strassenfeld mit Vorschlägen aus dem eigenen Bestand.
 *
 * Wer eine Strasse aus der Liste wählt, bekommt PLZ und Ort gleich mit – die
 * Adresse gehört zusammen, und sie zweimal zu tippen ist genau die Arbeit,
 * die diese Vorschläge abnehmen sollen.
 */
export function StrassenFeld({
  id,
  wert,
  aufWert,
  aufAdresse,
  bestand
}: {
  id: string
  wert: string
  aufWert: (strasse: string) => void
  /** Wird gerufen, wenn eine vollständige Adresse gewählt wurde. */
  aufAdresse?: (a: Adresse) => void
  bestand: ReadonlyArray<Adresse>
}): JSX.Element {
  const treffer = useMemo(() => strassenVorschlaege(wert, bestand), [wert, bestand])
  return (
    <Vorschlagsfeld
      id={id}
      wert={wert}
      aufWert={aufWert}
      aufAuswahl={(v) => {
        const a = bestand.find((x) => `${x.strasse}|${x.plz}|${x.ort}` === v.schluessel)
        if (a) aufAdresse?.(a)
      }}
      vorschlaege={treffer.map((a) => ({
        wert: a.strasse,
        haupt: a.strasse,
        neben: [a.plz, a.ort].filter(Boolean).join(' '),
        schluessel: `${a.strasse}|${a.plz}|${a.ort}`
      }))}
    />
  )
}

/**
 * Das Feldpaar PLZ / Ort – beide Felder, beide Listen, eine Komponente.
 *
 * Die Klassennamen kommen von aussen, weil die drei Masken verschieden
 * gebaut sind (row/narrow/grow im Büro, o-field in den Einstellungen,
 * rp-best-feld auf der öffentlichen Seite). Das Verhalten kommt von hier –
 * und das ist die Aufteilung, auf die es ankommt: Aussehen darf sich
 * unterscheiden, Bedienung nicht.
 */
export function OrtsFeldPaar({
  praefix,
  plz,
  ort,
  aufAenderung,
  ortschaften,
  klassen,
  hinweis = false,
  beschriftungen,
  ids
}: {
  praefix: string
  plz: string
  ort: string
  aufAenderung: (werte: { plz: string; ort: string }) => void
  ortschaften: ReadonlyArray<[string, string]>
  /**
   * Die Klassen der Hülle und der beiden Felder. `reihe: null` heisst: keine
   * eigene Hülle – dann liegen die zwei Felder direkt im Raster der Maske.
   * Ein leeres <div> dazwischen wäre dort eine Rasterzelle und würde die
   * Spalten verschieben.
   */
  klassen: { reihe: string | null; feld: string; plz?: string; ort?: string }
  /** Zeigt unter dem PLZ-Feld, woher die Vorschläge kommen. */
  hinweis?: boolean
  beschriftungen?: { plz?: string; ort?: string }
  /**
   * Die Kennungen der beiden Felder.
   *
   * Sie kommen von aussen, weil die Masken sie verschieden benannt haben
   * (k-zip, p-zip, s-zip, ex-plz) und weil an diesen Namen die
   * Abnahmeskripte hängen. Eine Umbenennung «weil es einheitlicher wäre»
   * würde hier eine Reihe grüner Prüfungen still rot machen.
   */
  ids?: { plz: string; ort: string }
}): JSX.Element {
  const idPlz = ids?.plz ?? `${praefix}-plz`
  const idOrt = ids?.ort ?? `${praefix}-ort`
  /*
    Jede Liste nimmt die Eingabe des eigenen Feldes – und wenn die leer ist,
    die des anderen.

    Das ist der Fall, der sonst untergeht: «Bellinzona» führt zu 6500, 6501
    und 6503 (zwei davon sind Postfachnummern). Nachziehen darf die Anwendung
    da nichts, sie müsste raten. Aber sie kann die drei im PLZ-Feld zur
    Auswahl stellen, sobald der Ort dasteht – und dann wählt der Betrieb.
  */
  const nachPlz = useMemo(() => vorschlaege(plz || ort, ortschaften), [plz, ort, ortschaften])
  const nachOrt = useMemo(() => vorschlaege(ort || plz, ortschaften), [ort, plz, ortschaften])

  const felder = (
    <>
      <div className={[klassen.feld, klassen.plz].filter(Boolean).join(' ')}>
        <label htmlFor={idPlz}>{beschriftungen?.plz ?? 'PLZ'}</label>
        <Vorschlagsfeld
          id={idPlz}
          inputMode="numeric"
          maxLength={4}
          wert={plz}
          aufWert={(w) => aufAenderung(plzGesetzt(w, ort, ortschaften))}
          // Aus der Liste gewählt heisst: beides ist bekannt. Dann wird der
          // Ort gesetzt, ohne die Zurückhaltung von plzGesetzt – wer klickt,
          // hat entschieden.
          aufAuswahl={(v) => aufAenderung({ plz: v.wert, ort: v.neben ?? ort })}
          vorschlaege={nachPlz.map((o) => ({
            wert: o.plz,
            haupt: o.plz,
            neben: o.ort,
            schluessel: `${o.plz} ${o.ort}`
          }))}
          hinweis={hinweis ? ortsHinweis(ortschaften) : undefined}
        />
      </div>
      <div className={[klassen.feld, klassen.ort].filter(Boolean).join(' ')}>
        <label htmlFor={idOrt}>{beschriftungen?.ort ?? 'Ort'}</label>
        <Vorschlagsfeld
          id={idOrt}
          wert={ort}
          aufWert={(w) => aufAenderung(ortGesetzt(w, plz, ortschaften))}
          aufAuswahl={(v) => aufAenderung({ plz: v.neben ?? plz, ort: v.wert })}
          vorschlaege={nachOrt.map((o) => ({
            wert: o.ort,
            haupt: o.ort,
            neben: o.plz,
            schluessel: `${o.plz} ${o.ort}`
          }))}
        />
      </div>
    </>
  )

  return klassen.reihe === null ? felder : <div className={klassen.reihe}>{felder}</div>
}

/**
 * Setzt die Postleitzahl und lässt den Ort folgen, wenn er folgen darf.
 *
 * Steht hier und nicht in den Bildschirmen, weil die Regel («nur wenn das
 * Ortsfeld leer ist oder noch einen bekannten Ort trägt») an vier Stellen
 * viermal leicht anders ausgefallen wäre.
 */
export function plzGesetzt(
  wert: string,
  ort: string,
  ortschaften: ReadonlyArray<[string, string]>
): { plz: string; ort: string } {
  const treffer = ortZuPlz(wert, ortschaften)
  if (treffer && ortDarfFolgen(ort, ortschaften)) return { plz: wert, ort: treffer }
  return { plz: wert, ort }
}

/**
 * Setzt den Ort und lässt die Postleitzahl folgen, wenn sie eindeutig ist.
 *
 * Der Weg, den fast jeder nimmt: Ort tippen, Zahl soll dastehen. Er folgt
 * derselben Zurückhaltung wie der umgekehrte – gefolgt wird nur, wenn es
 * nichts zu raten gibt und die Postleitzahl nicht von Hand gesetzt wurde.
 */
export function ortGesetzt(
  wert: string,
  plz: string,
  ortschaften: ReadonlyArray<[string, string]>
): { plz: string; ort: string } {
  const treffer = plzZuOrt(wert, ortschaften)
  if (treffer && plzDarfFolgen(plz, ortschaften)) return { plz: treffer, ort: wert }
  return { plz, ort: wert }
}

/** Der Hinweis unter dem Feld – sagt, woher die Vorschläge kommen. */
export function ortsHinweis(ortschaften: ReadonlyArray<[string, string]>): string {
  if (verzeichnisVorhanden())
    return 'Vorschläge aus dem Ortschaftenverzeichnis und Ihrem Bestand – PLZ oder Ort tippen.'
  if (ortschaften.length > 0) return `Vorschläge aus ${ortschaften.length} eigenen Adressen.`
  return 'Noch keine Adressen erfasst – der Ort muss von Hand eingetragen werden.'
}
